/**
 * Which join request the full-screen "X wants to join" prompt shows in a
 * Firmio Meet (components/meet/AdmitRequestOverlay, web and mobile). Pure, so
 * the rules are unit-tested (mobile/test/meetAdmitQueue.test.ts).
 *
 *   - The person waiting longest comes first.
 *   - "Mai târziu" hides one person's prompt; they stay in the waiting list
 *     (people panel and the badge) and can be admitted from there.
 *   - Someone admitted or denied on another colleague's screen, or who gave
 *     up, leaves the list at the next poll and their prompt closes by itself.
 */

export interface WaitingPerson {
  id: string;
  joinedAt: string;
}

export interface AdmitPrompt<T extends WaitingPerson> {
  /** The request on screen. */
  current: T;
  /** Everyone else still waiting (hidden ones included), for "+N în așteptare". */
  others: number;
  /** Everyone waiting, for "Acceptă toți (N)". */
  total: number;
}

function byArrival<T extends WaitingPerson>(waiting: readonly T[]): T[] {
  return [...waiting].sort(
    (a, b) => a.joinedAt.localeCompare(b.joinedAt) || a.id.localeCompare(b.id),
  );
}

/** The prompt to show, or null when nobody waits or everyone was hidden. */
export function nextAdmitRequest<T extends WaitingPerson>(
  waiting: readonly T[],
  hidden: ReadonlySet<string>,
): AdmitPrompt<T> | null {
  const ordered = byArrival(waiting);
  const current = ordered.find((p) => !hidden.has(p.id));
  if (!current) return null;
  return { current, others: ordered.length - 1, total: ordered.length };
}

/**
 * Ids that were not in the list at the previous poll: each arrival knocks
 * once. `previous === null` is the first look after entering the room, and
 * whoever is already waiting counts as new, since they are waiting on you too.
 */
export function newArrivals(
  previous: ReadonlySet<string> | null,
  waiting: readonly WaitingPerson[],
): string[] {
  return waiting.filter((p) => !previous || !previous.has(p.id)).map((p) => p.id);
}

/**
 * Forget hidden ids of people no longer waiting, so someone who leaves and
 * knocks again later gets a fresh prompt. Returns the same set when nothing
 * changed, so it can feed React state without a re-render loop.
 */
export function pruneHidden(
  hidden: ReadonlySet<string>,
  waiting: readonly WaitingPerson[],
): ReadonlySet<string> {
  const still = new Set(waiting.map((p) => p.id));
  const next = new Set([...hidden].filter((id) => still.has(id)));
  return next.size === hidden.size ? hidden : next;
}
